<!--
An anutocomplete field on which all vee validation rules can be applied throught the "rules" prop.
See vee-validationrules.ts for list of available rules

Example use : 
 <AutoCompleteWithValidation
    rules="required"
    :tooltip="$t('input-email-tooltip')"
    :label="$t('input-email')"
    placeholder="e.g. EXAMPLE VALUE"
    v-model="something"
    :data="listofthings"
    :emptyMessage="Message when nothing is found"
  />
-->
<template>
  <ValidationProvider
    v-slot="{ errors, valid, handleChange, handleBlur }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="($attrs.name as string) || ($attrs.label as string)"
    :rules="rules"
    :model-value="innerValue"
    @update:model-value="updateValue"
  >
    <b-field
      :type="{
        'is-danger': errors[0],
        'is-success': valid,
      }"
      :message="errors"
      class="AutoCompleteWithValidation-field"
    >
      <template #label>
        <FormInputLabel
          :label="($attrs.label as string) || ''"
          :is-mandatory="isMandatory"
          :tooltip="tooltip"
        />
      </template>

      <b-autocomplete
        v-model="innerValue"
        open-on-focus
        :placeholder="placeholder"
        :data="data"
        icon="search"
        clearable
        @update:model-value="
          (val: any) => {
            handleChange && handleChange(val)
          }
        "
        @blur="(event: any) => handleBlur && handleBlur(event)"
        @select="emitSelect"
      >
        <template v-if="Boolean(emptyMessage)" #empty>
          <a v-if="$attrs.clickOnEmpty" @click="onEmptyResult">
            <span> {{ emptyMessage }} </span>
          </a>
          <div v-if="!$attrs.clickOnEmpty">{{ emptyMessage }}</div>
        </template>
      </b-autocomplete>
    </b-field>
  </ValidationProvider>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "./FormInputLabel.vue"
import { BField, BAutocomplete } from "buefy"

export default defineComponent({
  name: "AutoCompleteWithValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    BField,
    BAutocomplete,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      required: false,
      default: "",
    },
    rules: {
      type: [String, Array],
      required: false,
      default: "",
    },
    modelValue: {
      type: String,
      required: false,
      default: "",
    },
    tooltip: {
      type: String,
      required: false,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    emptyMessage: {
      type: String,
      default: "",
    },
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  emits: ["update:modelValue", "manual-change", "select", "empty-result"],
  data() {
    return {
      innerValue: "",
      isMandatory: false,
    }
  },
  watch: {
    innerValue(newVal: string, _oldValue: string): void {
      this.$emit("update:modelValue", newVal)
    },
    modelValue(newVal: string, _oldValue: string): void {
      this.innerValue = newVal
      if (newVal != _oldValue) {
        this.$emit("manual-change", newVal)
      }
    },
  },
  created(): void {
    if (this.modelValue) {
      this.innerValue = this.modelValue
    }
    // Handle both string and array formats for rules
    if (this.rules) {
      if (Array.isArray(this.rules)) {
        this.isMandatory = this.rules.includes("required")
      } else {
        this.isMandatory = this.rules.includes("required")
      }
    } else {
      this.isMandatory = false
    }
  },
  methods: {
    updateValue(newValue: string): void {
      this.innerValue = newValue
      // Émettre immédiatement pour synchroniser avec le parent
      this.$emit("update:modelValue", newValue)
    },
    emitSelect(newVal: string): void {
      this.$emit("select", newVal)
    },

    onEmptyResult(): void {
      this.$emit("empty-result")
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.AutoCompleteWithValidation-field {
  margin-bottom: $input-field-margin-bot;
}
</style>
